import { useEffect, useState } from "react";
import { complianceApi } from "../../services/complianceApi";
import { Card } from "../../components/common/Card";
import { Badge } from "../../components/common/Badge";
import { DataTable } from "../../components/common/DataTable";
import { GapNotice } from "../../components/common/GapNotice";
import { Button } from "../../components/common/Button";
import { Spinner } from "../../components/common/Spinner";
import { AdminActionButton } from "../../components/common/AdminActionButton";
import { ROUTES } from "../../routes";
import { Link } from "react-router-dom";

/** GDPR request queue (GET /management/gdpr/requests?status=Pending) is real.
 * Export requests are fulfilled synchronously by the user-facing app and
 * essentially never land here Pending, so this queue is mostly Deletion
 * requests. Approving a Deletion soft-deletes that user's account and
 * force-logs them out server-side -- effectively irreversible from this
 * screen -- so Approve goes through the same confirm+reauth pattern as other
 * irreversible admin actions in this app (compare UserManagementPage's
 * Delete Account via AdminActionButton). */
export function CompliancePage() {
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [notesByRequest, setNotesByRequest] = useState({});

  useEffect(() => {
    refresh();
  }, []);

  function refresh() {
    complianceApi
      .gdprRequests({ status: "Pending" })
      .then((r) => setRequests(r.requests))
      .catch((err) => setError(err.apiMessage || "Failed to load GDPR request queue"));
  }

  async function decide(requestId, approve) {
    setActionError(null);
    try {
      await complianceApi.decideGdprRequest(requestId, { approve, notes: notesByRequest[requestId] || undefined });
      setNotesByRequest((prev) => {
        const next = { ...prev };
        delete next[requestId];
        return next;
      });
      refresh();
    } catch (err) {
      setActionError(err.apiMessage || "Failed to record decision");
    }
  }

  return (
    <div>
      <h1>Compliance &amp; Auditing</h1>

      <Card title="Audit Log" className="section-spacing">
        <p>Full searchable/filterable audit log with CSV/PDF export lives on its own page.</p>
        <Link to={ROUTES.AUDIT_LOG}><Button size="sm" variant="secondary">Open Audit Log</Button></Link>
      </Card>

      <Card title="GDPR Requests (Deletion & Export)" className="section-spacing">
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          Showing Pending requests awaiting a decision. Export requests are fulfilled synchronously by the
          user-facing app and rarely appear here -- this queue is mostly account-deletion requests.
        </p>

        {actionError && <p style={{ color: "var(--color-danger)" }}>{actionError}</p>}
        {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
        {!error && !requests && <Spinner />}

        {!error && requests && (
          <DataTable
            rows={requests}
            rowKey="request_id"
            emptyTitle="No pending GDPR requests"
            columns={[
              { key: "email", header: "User" },
              {
                key: "request_type",
                header: "Type",
                render: (r) => <Badge tone={r.request_type === "Deletion" ? "danger" : "info"}>{r.request_type}</Badge>,
              },
              { key: "requested_date", header: "Requested", render: (r) => new Date(r.requested_date).toLocaleDateString() },
              { key: "status", header: "Status", render: (r) => <Badge tone="warning">{r.status}</Badge> },
              { key: "notes", header: "Notes", render: (r) => r.notes || "—" },
              {
                key: "actions",
                header: "Decision",
                render: (r) => (
                  <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
                    <input
                      placeholder="Notes (optional)"
                      value={notesByRequest[r.request_id] || ""}
                      onChange={(e) => setNotesByRequest((prev) => ({ ...prev, [r.request_id]: e.target.value }))}
                      className="touch-target"
                      style={{ minWidth: "10rem" }}
                    />
                    <AdminActionButton
                      label="Approve"
                      danger={r.request_type === "Deletion"}
                      confirmTitle={`Approve ${r.request_type.toLowerCase()} request for ${r.email}?`}
                      confirmBody={
                        r.request_type === "Deletion" ? (
                          <p>
                            This will soft-delete <strong>{r.email}</strong>&apos;s account and immediately
                            force-log-them-out server-side. This is effectively irreversible from this screen -- make
                            sure this request is genuine before confirming.
                          </p>
                        ) : (
                          <p>This marks the export request for {r.email} as approved.</p>
                        )
                      }
                      onConfirm={() => decide(r.request_id, true)}
                    />
                    <AdminActionButton
                      label="Deny"
                      danger={false}
                      confirmTitle={`Deny request for ${r.email}?`}
                      confirmBody={<p>This marks the request as Denied. The user can submit a new request later.</p>}
                      onConfirm={() => decide(r.request_id, false)}
                    />
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Card title="Compliance Dashboard (HIPAA/GDPR Status)">
        <GapNotice>
          Would summarize posture across: RLS coverage (real, V019), Always Encrypted status (metadata-only today,
          per V017's own header comment -- full encryption requires a separate PowerShell step that hasn't been run),
          audit log retention (currently unbounded/"forever," no explicit 7-year purge policy configured), and GDPR
          request fulfillment (now real via the queue above). No single endpoint aggregates these into one dashboard
          view yet.
        </GapNotice>
      </Card>
    </div>
  );
}
